import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FileText, MessageSquare, Eye, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalReviews: 0,
    publishedReviews: 0,
    pendingComments: 0
  });
  const [recentReviews, setRecentReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reviewsRes, commentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/reviews`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/comments`, { withCredentials: true })
      ]);

      const reviews = reviewsRes.data;
      const comments = commentsRes.data;

      setStats({
        totalReviews: reviews.length,
        publishedReviews: reviews.filter(r => r.published).length,
        pendingComments: comments.filter(c => !c.approved).length
      });

      setRecentReviews(reviews.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div data-testid="loading-state">Loading...</div>;
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-4xl font-bold">Dashboard</h1>
        <Link
          to="/admin/reviews/create"
          data-testid="create-review-btn"
          className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-sm hover:opacity-90 smooth-transition"
        >
          <Plus size={20} />
          <span>Create Review</span>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div data-testid="stat-total-reviews" className="bg-card border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Total Reviews</p>
              <p className="text-3xl font-bold">{stats.totalReviews}</p>
            </div>
            <div className="p-3 bg-primary/10 rounded-sm">
              <FileText size={24} className="text-primary" />
            </div>
          </div>
        </div>

        <div data-testid="stat-published-reviews" className="bg-card border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Published</p>
              <p className="text-3xl font-bold">{stats.publishedReviews}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-sm">
              <Eye size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div data-testid="stat-pending-comments" className="bg-card border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Pending Comments</p>
              <p className="text-3xl font-bold">{stats.pendingComments}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-sm">
              <MessageSquare size={24} className="text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Reviews */}
      <div>
        <h2 className="font-serif text-2xl font-bold mb-6">Recent Reviews</h2>
        <div className="bg-card border border-border">
          <table className="w-full">
            <thead className="border-b border-border">
              <tr>
                <th className="text-left p-4 font-semibold">Title</th>
                <th className="text-left p-4 font-semibold">Category</th>
                <th className="text-left p-4 font-semibold">Rating</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-left p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentReviews.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-muted-foreground">
                    No reviews yet
                  </td>
                </tr>
              ) : (
                recentReviews.map((review) => (
                  <tr key={review.id} data-testid={`review-row-${review.id}`} className="border-b border-border hover:bg-muted">
                    <td className="p-4">{review.title}</td>
                    <td className="p-4">{review.category?.name}</td>
                    <td className="p-4">{review.rating}/5</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs rounded-sm ${
                        review.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {review.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="p-4">
                      <Link
                        to={`/admin/reviews/${review.id}/edit`}
                        className="text-primary hover:underline text-sm"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;